// FILE: src/components/StudyPlanEditor.js
//
// Phase D — portal overlay for setting (or regenerating) a study plan.
//
// Inputs:
//   • target exam date  → stamped on learner_profiles.target_exam_date
//   • optional personal target slider (65–90)
//                       → stamped on learner_profiles.target_score_pct
//                       Only shown when activeCert is selected (§6.10.3
//                       lock — "All" view uses cert defaults).
//
// On Save:
//   1. Update learner_profiles with target_exam_date + target_score_pct.
//   2. Call generateStudyPlan() with the current readiness.
//   3. saveStudyPlan() — archives any active plan and inserts the new one
//      + its activities.
//   4. Closes; caller refreshes whatever surfaced it.
//
// Tone is calm-mentor — "Set your exam date and we'll suggest a path." No
// pressure, no streak language. The slider copy is informational.

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './StudyPlanEditor.module.css';
import { getExamConfig } from '../../config/exams';
import { generateStudyPlan, saveStudyPlan } from '../../services/studyPlanService';
import { updateLearnerProfile } from '../../services/learnerProfileService';

export default function StudyPlanEditor({
  isOpen,
  onClose,
  onSaved,
  activeCert,             // 'pmp' | 'cpmai'
  readiness,              // computeReadiness() result (may be null for new learners)
  initialTargetDate,      // ISO date string, optional
  initialPersonalTarget,  // integer, optional
}) {
  const examConfig    = useMemo(() => getExamConfig(activeCert), [activeCert]);
  const defaultTarget = examConfig?.pass_target_pct || 75;

  const [targetDate, setTargetDate]           = useState(initialTargetDate || '');
  const [personalTarget, setPersonalTarget]   = useState(
    typeof initialPersonalTarget === 'number' ? initialPersonalTarget : defaultTarget,
  );
  const [usePersonalTarget, setUsePersonal]   = useState(
    typeof initialPersonalTarget === 'number',
  );
  const [busy, setBusy]                       = useState(false);
  const [error, setError]                     = useState(null);
  const [preview, setPreview]                 = useState(null);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Recompute the preview when inputs change. Pure; no DB hit.
  useEffect(() => {
    if (!isOpen || !targetDate) { setPreview(null); return; }
    try {
      const today = new Date().toISOString().slice(0, 10);
      const p = generateStudyPlan({
        examId: activeCert, today, targetDate, readiness, examConfig,
      });
      setPreview(p);
    } catch (_) {
      setPreview(null);
    }
  }, [isOpen, targetDate, readiness, activeCert, examConfig]);

  if (!isOpen) return null;

  async function handleSave(e) {
    e?.preventDefault?.();
    setBusy(true); setError(null);

    if (!targetDate) {
      setBusy(false); setError('Pick a target exam date to continue.');
      return;
    }

    const profilePatch = {
      target_exam_id:   activeCert,
      target_exam_date: targetDate,
      target_score_pct: usePersonalTarget ? personalTarget : null,
    };

    // Update profile fields. Failures here are surfaced; they affect Save.
    try {
      const userId = await currentUserId();
      if (!userId) {
        setError('You need to be signed in to save a plan.');
        setBusy(false);
        return;
      }
      await updateLearnerProfile(userId, profilePatch);
    } catch (err) {
      setError(err?.message || 'Profile update failed — please try again.');
      setBusy(false);
      return;
    }

    // Build + save the plan.
    const today = new Date().toISOString().slice(0, 10);
    const plan = generateStudyPlan({
      examId: activeCert, today, targetDate, readiness, examConfig,
    });
    const result = await saveStudyPlan({
      examId: activeCert,
      targetDate,
      activities: plan.activities,
      inputsSnapshot: {
        readiness_status: readiness?.status || 'not_started',
        readiness_score:  readiness?.score  || 0,
        weak_phases:      readiness?.weakPhases || [],
        mode:             plan.mode,
        target_score_pct: usePersonalTarget ? personalTarget : defaultTarget,
        cadence:          examConfig?.study_cadence || null,
      },
    });

    setBusy(false);
    if (!result.plan_id) {
      setError(result.error || 'Could not save the plan — please try again.');
      return;
    }
    onSaved?.({ planId: result.plan_id, activityCount: result.count });
    onClose?.();
  }

  const effectiveTarget = usePersonalTarget ? personalTarget : defaultTarget;
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10); // tomorrow

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="study-plan-editor-backdrop"
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="spe-title">
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close study plan editor"
          onClick={() => onClose?.()}
          data-testid="study-plan-editor-close"
        >
          ×
        </button>

        <p className={styles.eyebrow}>Your Certification Journey</p>
        <h2 id="spe-title" className={styles.title}>
          {initialTargetDate ? 'Update your study plan' : 'Set your exam date'}
        </h2>
        <p className={styles.lead}>
          Pick a target date and we&apos;ll lay out a suggested path for {examConfig?.displayName || activeCert.toUpperCase()}. You can recalibrate any time.
        </p>

        <form onSubmit={handleSave} className={styles.form}>
          <label className={styles.label}>
            Target exam date
            <input
              type="date"
              value={targetDate}
              min={minDate}
              onChange={(e) => setTargetDate(e.target.value)}
              required
              disabled={busy}
              data-testid="study-plan-editor-date"
            />
          </label>

          <div className={styles.targetBlock} data-testid="study-plan-editor-target-block">
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={usePersonalTarget}
                onChange={(e) => setUsePersonal(e.target.checked)}
                disabled={busy}
                data-testid="study-plan-editor-personal-target-toggle"
              />
              Set a personal score target
            </label>
            <p className={styles.targetCaption}>
              Default is {defaultTarget}% (the Exam Ready threshold for {examConfig?.displayName || activeCert.toUpperCase()}).
              {' '}A personal target shifts the line on your Performance History chart only.
            </p>
            {usePersonalTarget && (
              <div className={styles.sliderRow}>
                <input
                  type="range"
                  min={65}
                  max={90}
                  step={1}
                  value={personalTarget}
                  onChange={(e) => setPersonalTarget(Number(e.target.value))}
                  disabled={busy}
                  className={styles.slider}
                  data-testid="study-plan-editor-personal-target-slider"
                />
                <span className={styles.sliderValue}>{personalTarget}%</span>
              </div>
            )}
            <p className={styles.targetSummary}>
              Effective target: <strong>{effectiveTarget}%</strong>
            </p>
          </div>

          {error && (
            <div className={styles.errorBox} role="alert" data-testid="study-plan-editor-error">
              {error}
            </div>
          )}

          {preview && preview.activities && preview.activities.length > 0 && (
            <PlanPreview preview={preview} />
          )}

          <div className={styles.actionRow}>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy || !targetDate}
              data-testid="study-plan-editor-save"
            >
              {busy ? 'Saving…' : (initialTargetDate ? 'Regenerate plan' : 'Save and start')}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => onClose?.()}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>

        <p className={styles.footnote}>
          Plans are frozen on save — you can mark activities complete or regenerate the plan any time.
        </p>
      </div>
    </div>,
    document.body,
  );
}

function PlanPreview({ preview }) {
  if (!preview) return null;
  const counts = countActivityTypes(preview.activities);
  return (
    <div className={styles.preview} data-testid="study-plan-editor-preview">
      <h3 className={styles.previewHeading}>Plan preview</h3>
      <p className={styles.previewSummary}>
        {preview.daysUntilExam} days until exam
      </p>
      <ul className={styles.previewList}>
        {counts.practice  > 0 && <li>{counts.practice} practice sessions</li>}
        {counts.targeted  > 0 && <li>{counts.targeted} targeted sessions{preview.weakPhases?.length ? ` (focusing on ${preview.weakPhases.slice(0,2).join(', ')})` : ''}</li>}
        {counts.full      > 0 && <li>{counts.full} full simulation{counts.full === 1 ? '' : 's'}</li>}
        {counts.review    > 0 && <li>{counts.review} review days</li>}
        {counts.rest      > 0 && <li>{counts.rest} rest days</li>}
        {counts.reading   > 0 && <li>{counts.reading} official reading sessions</li>}
        {counts.logistics > 0 && <li>{counts.logistics} exam-logistics checkpoints</li>}
      </ul>
    </div>
  );
}

function countActivityTypes(acts) {
  const out = { practice: 0, targeted: 0, full: 0, review: 0, rest: 0, reading: 0, logistics: 0, exam_day: 0 };
  for (const a of acts || []) if (out[a.activity_type] != null) out[a.activity_type]++;
  return out;
}

async function currentUserId() {
  // Lazy import keeps testing simpler.
  const { supabase } = await import('../../services/supabaseClient');
  try {
    // Local session read (no network); avoids the getUser() null-on-failure
    // fault wrongly blocking a signed-in learner from saving a plan.
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch (_) {
    return null;
  }
}
